'use client';

import { Button } from '@flipster/react/design-system';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { center, stack, vstack } from '@flipster/styled-system/patterns';

import { Asset } from '@web/graphql/types.generated';

import { slugToNameMapper } from '../constants';
import { ImageOrLottie } from '../image-or-lottie';
import { useSpookyTreatsFestivalLuckyDraw } from '../useLuckyDraw';

export interface HandleExchangeProps {
  fromSlug: string;
  toSlug: string;
  cost: number;
}

interface ItemCardProps {
  fromSlug: string;
  toSlug: string;
  tokenIcon: Asset;
  index: number;
  handleButtonClick: ({ fromSlug, toSlug, cost }: HandleExchangeProps) => void;
}

export const ItemCard = ({ fromSlug, toSlug, tokenIcon, index, handleButtonClick }: ItemCardProps) => {
  const { status, luckyDrawData, getExchangeCost } = useSpookyTreatsFestivalLuckyDraw();

  const data = luckyDrawData.find((data) => data?.id === toSlug);
  const cost = getExchangeCost(fromSlug, toSlug)?.cost ?? 0;
  const buttonLabel = index === 0 ? 'View missions' : 'Exchange';
  const getDescription = (index: number) => {
    if (index === 0) return 'Complete missions\nto earn candies';
    return `Exchange ${cost} ${slugToNameMapper[fromSlug].plural}\nto get 1`;
  };

  return (
    <li
      key={slugToNameMapper[toSlug]?.singular}
      className={vstack({
        flex: 1,
        w: '100%',
        py: 24,
        px: 16,
        rounded: 28,
        bg: 'linear-gradient(#262A51, #606797)',
        gap: { base: 8, tablet: 12 },
        tablet: { maxW: 360 },
      })}
    >
      <div
        className={stack({
          pb: { base: 8, tablet: 28 },
          gap: 8,
        })}
      >
        <p
          className={css({
            textStyle: 'Title/L',
            color: 'content.default.level-5',
            textAlign: 'center',
          })}
        >
          {slugToNameMapper[toSlug]?.singular}
        </p>
        <p
          className={css({
            textStyle: 'Text/L/Strong',
            color: 'content.default.level-5',
            textAlign: 'center',
          })}
        >
          You have: <Formatted.Number value={data?.token} />
        </p>
      </div>
      <div
        className={center({
          flex: 1,
          aspectRatio: '1 / 1',
          w: '100%',
          maxW: { base: 120, tablet: 200 },
          maxH: { base: 120, tablet: 200 },
        })}
      >
        <ImageOrLottie url={tokenIcon?.url ?? ''} alt='token icon' css={{ w: '100%', h: '100%' }} />
      </div>
      <p
        className={css({
          textStyle: 'Title/XS',
          color: 'content.default.level-5',
          textAlign: 'center',
          whiteSpace: 'pre-line',
        })}
      >
        {getDescription(index)}
      </p>
      <Button
        size='large'
        label={buttonLabel}
        onClick={() => handleButtonClick({ fromSlug, toSlug, cost })}
        disabled={status !== 'ONGOING'}
      />
    </li>
  );
};
